import { A2UIClientEventMessage, A2UIProvider, A2UIRenderer, injectStyles, useA2UI } from '@copilotkit/a2ui-renderer';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { A2uiOperation, A2uiUserAction } from '~/components/AIInsightsModal/AIInsightsModal.utils';

import { prepareOperationsForStore } from './operationsEnvelope';
import { visitiqCatalog } from './visitiqCatalog';
import { FallbackCard, SurfaceContainer } from './visitiqCatalog.styles';

// The renderer's stylesheet is global and idempotent; inject it once for the whole app.
let stylesInjected = false;

interface FeederProps {
  operations: A2uiOperation[];
}

interface SurfaceErrorBoundaryProps {
  fallbackMessage: string;
  children: React.ReactNode;
}

interface SurfaceErrorBoundaryState {
  hasError: boolean;
}

// React error boundaries must be class components. Catches renderer throws so one bad surface
// can't blank the whole drawer subtree. The translated message is passed in as a prop since
// class components can't use the useTranslation hook.
class SurfaceErrorBoundary extends React.Component<SurfaceErrorBoundaryProps, SurfaceErrorBoundaryState> {
  state: SurfaceErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): SurfaceErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error): void {
    console.warn('A2uiSurface renderer error', error);
  }

  render(): React.ReactNode {
    if (this.state.hasError) {
      return <FallbackCard>{this.props.fallbackMessage}</FallbackCard>;
    }
    return this.props.children;
  }
}

// Feeds the surface operations into the provider's store. Must live UNDER A2UIProvider so
// useA2UI() resolves the context. Renders nothing itself.
const SurfaceFeeder = ({ operations }: FeederProps): null => {
  const { processMessages } = useA2UI();
  // The sidebar rebuilds a fresh `operations` array on every conversation fetchMore (new array
  // reference, identical content) for every surface already on screen. Reprocessing would re-send
  // the surface's createSurface op for a surface that already exists, which the A2UI store throws
  // on (A2uiStateError) — aborting the rest of that batch and silently dropping any other op in it.
  // Only (re)feed the store when the operations actually changed, so an unchanged surface is fed
  // exactly once and live-entered form state is never reset by a redundant reprocess.
  const lastProcessedRef = useRef<string | null>(null);
  useEffect(() => {
    const serialized = JSON.stringify(operations);
    if (serialized === lastProcessedRef.current) return;
    lastProcessedRef.current = serialized;
    processMessages(prepareOperationsForStore(serialized));
  }, [operations, processMessages]);
  return null;
};

export type SurfaceDataModel = Record<string, unknown>;

// The slice of web_core's surface DataModel the observer uses; `getSurface` is untyped.
interface ObservableDataModel {
  subscribe: (
    path: string,
    onChange: (value: SurfaceDataModel | undefined) => void,
  ) => { value: SurfaceDataModel | undefined; unsubscribe: () => void };
}

interface DataModelObserverProps {
  surfaceId: string;
  onChange: (dataModel: SurfaceDataModel) => void;
}

// Reports the surface's live data model (what the user has entered so far) as it changes, without
// waiting for a submit. Must live UNDER A2UIProvider, useA2UI() re-renders it once the feeder has
// created the surface, which is when the subscription can attach. A root subscription fires on any
// nested edit (the data model notifies ancestors with a fresh copy of their value).
const SurfaceDataModelObserver = ({ surfaceId, onChange }: DataModelObserverProps): null => {
  const { getSurface } = useA2UI();
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const dataModel = (getSurface(surfaceId) as { dataModel?: ObservableDataModel } | undefined)?.dataModel;

  useEffect(() => {
    if (!dataModel) return undefined;
    const subscription = dataModel.subscribe('/', (value) => onChangeRef.current(value ?? {}));
    onChangeRef.current(subscription.value ?? {});
    return () => subscription.unsubscribe();
  }, [dataModel]);
  return null;
};

interface Props {
  operations: A2uiOperation[];
  surfaceId: string;
  // Resolve `false` when the submission could not be dispatched to the agent (out of AI credits, a
  // turn still streaming, a failed send): the local lock is released so the user can retry with the
  // values they already entered. Anything else (including `void`) keeps the surface locked.
  onSubmit: (action: A2uiUserAction) => void | Promise<boolean>;
  // Dim + block interaction (submit becomes a no-op). Set by the parent whenever a submission could
  // not be dispatched anyway — a turn is streaming, the user is out of AI credits — and for a
  // surface the user has already submitted (so used surfaces stay locked while others in the
  // conversation remain interactive). Also OR'd with local `submitted`.
  disabled?: boolean;
  // Live data model on every user edit, for parents that derive something from in-progress values.
  onDataModelChange?: (dataModel: SurfaceDataModel) => void;
}

// Renders one agent-composed A2UI surface (a form/picker) inline in the chat using the stock
// @copilotkit/a2ui-renderer standalone stack (no CopilotKit provider). On submit the library
// hands us `{ name, surfaceId, context }` — context is the surface's data model — which the
// sidebar maps onto the agent's a2uiSubmission transport (formId = surfaceId, values = context).
const A2uiSurface = ({ operations, surfaceId, onSubmit, disabled = false, onDataModelChange }: Props): JSX.Element => {
  const { t } = useTranslation();
  const fallbackMessage = t('aiInsights.a2ui.unavailable');
  const [submitted, setSubmitted] = useState(false);
  // Refs so the (potentially provider-memoized) action handler never reads stale state.
  const submittedRef = useRef(false);
  const disabledRef = useRef(disabled);
  disabledRef.current = disabled;
  const onSubmitRef = useRef(onSubmit);
  onSubmitRef.current = onSubmit;

  useEffect(() => {
    if (!stylesInjected) {
      injectStyles();
      stylesInjected = true;
    }
  }, []);

  const handleAction = useCallback((message: A2UIClientEventMessage): void => {
    const action = message.userAction;
    if (!action || submittedRef.current || disabledRef.current) {
      return;
    }
    // Lock first so a double click can't re-send, then release it again if the parent reports the
    // submission never reached the agent — otherwise the entered values would be stuck in an inert
    // surface with no way to resend them.
    submittedRef.current = true;
    setSubmitted(true);
    void Promise.resolve<void | boolean>(
      onSubmitRef.current({
        name: action.name,
        surfaceId: action.surfaceId,
        sourceComponentId: action.sourceComponentId,
        context: action.context,
      }),
    ).then((accepted) => {
      if (accepted === false) {
        submittedRef.current = false;
        setSubmitted(false);
      }
    });
  }, []);

  const inert = submitted || disabled;
  return (
    // Native `inert` (not just $inert styling) blocks interaction inside portaled menus
    // (Dropdown/DateFilterButton render to <body>, escaping `pointer-events: none`) and removes
    // the subtree from keyboard focus/tab order. React 18's JSX types don't know `inert` yet, so
    // it's spread as a plain DOM attribute rather than passed as a typed prop.
    <SurfaceContainer $inert={inert} {...(inert ? { inert: '' } : {})}>
      <SurfaceErrorBoundary fallbackMessage={fallbackMessage}>
        <A2UIProvider catalog={visitiqCatalog} onAction={handleAction}>
          <SurfaceFeeder operations={operations} />
          {onDataModelChange && <SurfaceDataModelObserver surfaceId={surfaceId} onChange={onDataModelChange} />}
          <A2UIRenderer surfaceId={surfaceId} fallback={<FallbackCard>{fallbackMessage}</FallbackCard>} />
        </A2UIProvider>
      </SurfaceErrorBoundary>
    </SurfaceContainer>
  );
};

export default A2uiSurface;
